import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Loader } from '@playbookux/playbook-web-core';
import { ImageDiv } from '../../styles';
import ClickConfirmationPrompt from './click-confirmation-prompt';
import { ContentImage } from './styles';

/**
 * BEFORE YOU START CHANGING ANYTHING HERE
 * PLEASE SEE THE COMMENTS ON THIS FILE:
 * src/company/components/heatmap/index.jsx
 * BECAUSE IT IS VERY IMPORTANT
 * IF YOU MAKE ANY CHANGES HERE, IT WILL IMPACT ENTIRE HEATMAPS.JS FUNCTIONALITY
 */
export const FirstClickImage = (props) => {
  const { imageUrl, onChange, onGoToNextPage } = props;
  const [loading, setLoading] = useState(true);
  const [showConfirmPrompt, setPromptClickConfirmation] = useState(false);
  const [clickCoordinates, setClickCoordinates] = useState({ x: 0, y: 0 });
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  // preloading the image
  useEffect(() => {
    if (imageUrl) {
      const img = new Image();
      setLoading(true);
      img.src = imageUrl;
      img.onload = ((i) => {
        const { width, height } = i.target;
        setLoading(false);
        setImageDimensions({ width, height });
      });
      img.onerror = () => setLoading(false);
    }
  }, [imageUrl]);

  const handleImageLoad = () => {
    setLoading(false);
  };

  /**
   * We do these calculations due to the resize that the user can do on the screen
   * and if you are going to make changes, be aware that it could impact the functionality
   * of the heatmap in analytics surveys
   * @param {MouseEvent} e
   */
  const handleFirstClick = (e) => {
    const { target, clientX, clientY } = e;
    const rect = target.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const originalWidth = target.naturalWidth;
    const originalHeight = target.naturalHeight;

    const normalizedX = (x / rect.width) * originalWidth;
    const normalizedY = (y / rect.height) * originalHeight;

    const resizedX = (normalizedX / originalWidth) * imageDimensions.width;
    const resizedY = (normalizedY / originalHeight) * imageDimensions.height;

    setPromptClickConfirmation(true);
    setClickCoordinates({ x: resizedX, y: resizedY });
    onChange({ x: resizedX, y: resizedY });
  };

  const handleCancelClick = () => {
    setPromptClickConfirmation(false);
    setClickCoordinates({ x: 0, y: 0 });
    onChange({ x: 0, y: 0 });
  };

  return (
    <ContentImage>
      {loading && <Loader size="small" data-loader="first-click-loader" />}
      {!loading && (
        <ImageDiv
          onLoad={handleImageLoad}
          onError={handleImageLoad}
          src={imageUrl}
          onClick={(e) => handleFirstClick(e)}
          clickConfirmation={showConfirmPrompt}
        />
      )}
      {showConfirmPrompt && (
        <ClickConfirmationPrompt
          x={clickCoordinates.x}
          y={clickCoordinates.y}
          imageSizeX={imageDimensions.width}
          imageSizeY={imageDimensions.height}
          onCancel={handleCancelClick}
          onGoToNextPage={onGoToNextPage}
        />
      )}
    </ContentImage>
  );
};

FirstClickImage.defaultProps = {
  onGoToNextPage: () => { },
};

FirstClickImage.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onGoToNextPage: PropTypes.func,
};
